import React from 'react';
import { useStepCarousel } from '../hooks/useStepCarousel';
import { HowItWorksStep } from '../types/homepage';
const Chevron: React.FC<{
  direction: 'left' | 'right';
}> = ({
  direction
}) => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false" data-id="element-99" data-mp="1pwjlgy4t0rr8">
    <path d={direction === 'left' ? 'M15 18l-6-6 6-6' : 'M9 18l6-6-6-6'} data-id="element-100" data-mp="1zto7mxyngn81" />
  </svg>;
interface HowItWorksCarouselProps {
  steps: HowItWorksStep[];
  onEnlarge: (index: number, trigger: HTMLButtonElement) => void;
}
export const HowItWorksCarousel: React.FC<HowItWorksCarouselProps> = ({
  steps,
  onEnlarge
}) => {
  const {
    index,
    goTo,
    next,
    prev,
    isFirst,
    isLast,
    onKeyDown,
    onTouchStart,
    onTouchEnd
  } = useStepCarousel(steps.length);
  const step = steps[index];
  const count = steps.length;
  return <div role="region" aria-roledescription="carousel" aria-label="How care comes together, step by step" onKeyDown={onKeyDown} className="mt-10 md:mt-12" data-id="element-101" data-mp="xhmkvpl20t41">
      <nav aria-label="Steps" data-id="element-102" data-mp="skw6d6owe5kq">
        <ol className="-mx-6 flex gap-2 overflow-x-auto px-6 pb-2 md:mx-0 md:grid md:grid-cols-5 md:overflow-visible md:px-0" data-id="element-103" data-mp="nix1tthf92m7">
          {steps.map((s, i) => {
          const isActive = i === index;
          return <li key={s.title} className="flex-shrink-0 md:flex-shrink" data-id="element-104" data-mp="1g6swksnfkkgc" data-mpi={i}>
                <button type="button" onClick={() => goTo(i)} aria-current={isActive ? 'step' : undefined} className={`relative flex min-h-[48px] w-full items-center gap-3 whitespace-nowrap rounded-xl border-[1.5px] px-3 py-2 text-left text-sm font-medium transition-colors duration-200 ${isActive ? 'border-deep-juniper bg-white text-deep-juniper shadow-card' : 'border-transparent text-plum-gray hover:bg-white/70 hover:text-deep-juniper'}`} data-id="element-105" data-mp="1b8rcq5i6kh3t">
                  <span aria-hidden="true" className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full font-literata text-sm ${isActive ? 'bg-deep-juniper text-white' : 'border-[1.5px] border-plum-gray/40'}`} data-id="element-106" data-mp="3tdiqgvl0y6nk">
                    {i + 1}
                  </span>
                  <span className="md:truncate" data-id="element-107" data-mp="3am9hcnf61jwi">
                    <span className="sr-only" data-id="element-108" data-mp="2vrv83elelov1">Step {i + 1}: </span>
                    {s.shortLabel}
                  </span>
                </button>
              </li>;
        })}
        </ol>
      </nav>

      <div role="group" aria-roledescription="slide" aria-label={`${index + 1} of ${count}: ${step.title}`} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} className="mt-4 touch-pan-y overflow-hidden rounded-2xl bg-white shadow-card" data-id="element-109" data-mp="u1080ak5z3ii">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]" data-id="element-110" data-mp="2eisoanohrh3b">
          <div className="flex flex-col justify-center p-6 md:p-10 lg:p-12" data-id="element-111" data-mp="2t7p2l2h55qcd">
            <p className="text-sm font-medium text-plum-gray" data-id="element-112" data-mp="2og6xq3bzz5m4">
              Step {index + 1} of {count}
            </p>
            <h3 className="mt-3 text-2xl md:text-[1.75rem]" data-id="element-113" data-mp="326agulfkyj7c">{step.title}</h3>
            <p className="mt-4 max-w-[34rem] text-lg leading-relaxed text-plum-gray" data-id="element-114" data-mp="2y608x3889ipu">
              {step.description}
            </p>
          </div>

          <figure className="flex flex-col items-center justify-center border-t border-mist-sage/60 bg-sandstone/40 px-6 pb-6 pt-8 lg:min-h-[640px] lg:border-l lg:border-t-0 lg:px-10" data-id="element-115" data-mp="3c4q9wty642jh">
            <div className="flex min-h-[320px] w-full items-center justify-center lg:min-h-[540px]" data-id="element-116" data-mp="1hs9xfbes3s66">
              <img key={step.screen.src} src={step.screen.src} alt={step.screen.alt} className="h-auto max-h-[480px] w-auto max-w-full rounded-[20px] border border-mist-sage bg-white object-contain lg:max-h-[540px]" data-id="element-117" data-mp="1ctykusx3n3k9" />
            </div>
            <figcaption className="mt-4 text-center text-sm leading-relaxed text-plum-gray" data-id="element-118" data-mp="1o8icfwfnp4py">
              {step.screen.caption}
            </figcaption>
            <button type="button" onClick={e => onEnlarge(index, e.currentTarget)} className="mt-3 inline-flex min-h-[40px] items-center gap-2 rounded-lg px-3 text-sm font-medium text-deep-juniper transition-colors duration-200 hover:bg-white/70" data-id="element-119" data-mp="vjunnqvp7x7n">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false" data-id="element-120" data-mp="1a87keq2m1k37">
                <path d="M15 3h6v6" data-id="element-121" data-mp="2r0ma5x30uycp" />
                <path d="M9 21H3v-6" data-id="element-122" data-mp="2r1nxazxbbbas" />
                <path d="M21 3l-7 7" data-id="element-123" data-mp="2qyi7figv05e0" />
                <path d="M3 21l7-7" data-id="element-124" data-mp="2m0gpx1z30e4n" />
              </svg>
              Enlarge screen
              <span className="sr-only" data-id="element-125" data-mp="2h2f449a9u0ph">: {step.title}</span>
            </button>
          </figure>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between gap-4" data-id="element-126" data-mp="2c43w843je09w">
        <button type="button" onClick={prev} aria-disabled={isFirst} className={`inline-flex min-h-[48px] items-center gap-2 rounded-xl border-[1.5px] border-deep-juniper px-4 font-medium text-deep-juniper transition-colors duration-200 ${isFirst ? 'cursor-not-allowed opacity-40' : 'hover:bg-deep-juniper/5'}`} data-id="element-127" data-mp="27cluhdtovvnv">
          <Chevron direction="left" data-id="element-128" data-mp="1odho6ctj8fzw" />
          <span className="hidden sm:inline" data-id="element-129" data-mp="1oej16mu3zck9">Previous</span>
          <span className="sr-only sm:hidden" data-id="element-130" data-mp="1epvkr5xa8ypc">Previous step</span>
        </button>

        <p className="text-sm font-medium text-deep-juniper" aria-live="polite" aria-atomic="true" data-id="element-131" data-mp="1erymgn7ssg9w">
          <span aria-hidden="true" data-id="element-132" data-mp="1jmvv3t160nm9">
            {index + 1} / {count}
          </span>
          <span className="sr-only" data-id="element-133" data-mp="15r0ejtu8nom1">
            Step {index + 1} of {count}: {step.title}
          </span>
        </p>

        <button type="button" onClick={next} aria-disabled={isLast} className={`inline-flex min-h-[48px] items-center gap-2 rounded-xl border-[1.5px] border-deep-juniper px-4 font-medium text-deep-juniper transition-colors duration-200 ${isLast ? 'cursor-not-allowed opacity-40' : 'hover:bg-deep-juniper/5'}`} data-id="element-134" data-mp="19sly1y1a3157">
          <span className="hidden sm:inline" data-id="element-135" data-mp="1snk76i700dwi">Next</span>
          <span className="sr-only sm:hidden" data-id="element-136" data-mp="2ge6f89h05y6i">Next step</span>
          <Chevron direction="right" data-id="element-137" data-mp="2l5xuhnupkhhc" />
        </button>
      </div>
      <p className="sr-only" data-id="element-138" data-mp="2l78z1pvz9t9i">Use the left and right arrow keys to move between steps.</p>
    </div>;
};